// @givmo/app-admin — shared auth-screen shell (SaaS redesign, 2026-06-26).
//
// A polished split auth layout: a left brand panel (brand gradient + lockup + value prop, hidden on
// small screens) and a right pane hosting a centered white auth card. The card carries the page
// `testId` (so the guard/e2e selectors target each screen) and the brand lockup; each screen renders
// its own content column inside. Render-only — no auth logic. The `wide` prop widens the card for
// the multi-field sign-up screens.

import type { ReactNode } from 'react';
import { GivmoWordmark, GivmoLogomark, testIdProp } from '@givmo/design-system';

export interface AuthShellProps {
  children: ReactNode;
  testId: string;
  /** Optional wider column for the multi-field sign-up screens. */
  wide?: boolean;
}

export function AuthShell({ children, testId, wide = false }: AuthShellProps) {
  return (
    <div className="flex min-h-screen bg-surface-sunken" {...testIdProp(testId)}>
      {/* ── Left brand panel (lg+) ─────────────────────────────────────────────────────────────
          Lockup anchored top-left; the headline + subcopy sit as a block at ~58% height (no empty
          upper half); a brand-600→brand-800 diagonal gradient with soft radial glows (the literal
          grid is gone — it read as a placeholder). */}
      <aside className="relative hidden w-[44%] max-w-[560px] shrink-0 overflow-hidden lg:flex lg:flex-col">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 55% at 80% 0%, rgba(104,141,240,0.45) 0%, rgba(104,141,240,0) 55%),' +
              'radial-gradient(60% 50% at 0% 100%, rgba(24,36,70,0.65) 0%, rgba(24,36,70,0) 60%),' +
              'linear-gradient(145deg, var(--color-brand-600) 0%, var(--color-brand-700) 48%, var(--color-brand-800) 100%)',
          }}
        />
        {/* A single soft, blurred orb for depth — subtle, not a placeholder texture. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 top-32 size-[280px] rounded-full opacity-40 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(151,180,246,0.7) 0%, rgba(151,180,246,0) 70%)',
          }}
        />
        {/* Faint Givmo "G" mark watermark — anchors the flat lower-right of the panel. */}
        <GivmoLogomark
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -right-12 h-[360px] w-auto select-none text-white/[0.06]"
        />

        <div className="relative flex h-full flex-col p-44">
          {/* Lockup — anchored top-left. */}
          <div className="flex items-center">
            <GivmoWordmark className="h-[28px] w-auto text-white" />
          </div>

          {/* Headline block — pushed to ~58% via a top spacer, then a small bottom gap. */}
          <div className="mt-auto max-w-[400px] pb-16">
            <p className="font-sans text-s32 font-w700 leading-h12 tracking-givmo text-white">
              Run your company's giving program with confidence.
            </p>
            <p className="mt-16 font-sans text-s15 font-w400 leading-h15 tracking-givmo text-white/75">
              Approve matches, track donation impact, and manage your team — all from one admin
              portal.
            </p>
            {/* A trio of quiet trust points to fill the lower block tastefully. */}
            <ul className="mt-24 flex flex-col gap-10">
              {[
                'Real-time match approvals',
                'Donation impact reporting',
                'Role-based team access',
              ].map((point) => (
                <li
                  key={point}
                  className="flex items-center gap-10 font-sans text-s14 font-w400 tracking-givmo text-white/80"
                >
                  <span className="flex size-18 shrink-0 items-center justify-center rounded-full bg-white/15">
                    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
                      <path
                        d="M2.5 6.2l2.2 2.2 4.8-4.8"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <p className="font-sans text-s13 font-w400 tracking-givmo text-white/55">
            © {new Date().getFullYear()} Givmo. Charitable giving, made simple.
          </p>
        </div>
      </aside>

      {/* ── Right auth pane ────────────────────────────────────────────────────────────────── */}
      <main className="flex flex-1 flex-col items-center justify-center px-20 py-32">
        <div className={`flex w-full flex-col ${wide ? 'max-w-[34rem]' : 'max-w-[26rem]'}`}>
          {/* Compact brand lockup (shown above the card; the only brand mark on small screens). */}
          <div className="mb-24 flex items-center justify-center lg:hidden">
            <GivmoWordmark className="h-[24px] w-auto text-brand-500" />
          </div>

          <div className="flex flex-col gap-20 rounded-2xl border border-border-subtle bg-surface p-32 shadow-sm">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
